import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BsCheckCircleFill } from "react-icons/bs";
import Skeleton from "react-loading-skeleton";

import TemplateNavbar from "../../components/shared/TemplateNavbar/TemplateNavbar";
import ProductCard from "../../components/TemplateCard/ProductCard";
import Empty from "../../assets/images/appImages/category.png";

import { getImage, sortByAlphabetical } from "../../utils/Utils";
import { useGetAllCategoryListQuery } from "../../utils/api/category/category.api";
import { buildCatalogPath, getCatalogVendeur } from "../../catalog/catalogTenant";
import {
  useInfiniteProductList,
  useInfiniteScrollSentinel,
} from "../../utils/hooks";
const LIMIT = 12;

/* ── Skeletons ── */
const CategorySkeleton = () => (
  <>
    {Array(4).fill(null).map((_, i) => (
      <div key={i} className="category-item">
        <Skeleton className="category-item__image" width={80} height={80} />
        <Skeleton width={60} height={12} style={{ marginTop: 6 }} />
      </div>
    ))}
  </>
);

const ProductSkeleton = () => (
  <>
    {Array(6).fill(null).map((_, i) => (
      <div key={i} className="product-card">
        <div className="product-card__image-wrapper">
          <Skeleton className="product-card__image" height={160} />
        </div>
        <div className="product-card__body">
          <Skeleton width="70%" height={14} style={{ marginBottom: 6 }} />
          <Skeleton width="50%" height={14} style={{ marginBottom: 10 }} />
          <Skeleton width="100%" height={32} />
        </div>
      </div>
    ))}
  </>
);

/* ── Category Item ── */
const CategoryItem = ({ item, onClick, isActive }: any) => (
  <div
    className={`category-item ${isActive ? "category-item--active" : ""}`}
    onClick={onClick}
    style={{ cursor: "pointer" }}
  >
    {isActive && (
      <div className="category-item__check">
        <BsCheckCircleFill size={26} color="#f97316" />
      </div>
    )}
    <img
      src={item?.images ? getImage(item?.images) : Empty}
      alt={item?.nom ?? ""}
      className="category-item__image"
    />
    <span className="category-item__label">{item?.nom}</span>
  </div>
);

/* ── Template ── */

export default function Template() {
  const navigate = useNavigate();
  const [selectedCategorie, setSelectedCategorie] = useState<any>(null);

  const listQuery = useMemo(
    () => ({
      limit: LIMIT,
      categorie: selectedCategorie?.slug ?? "",
      sous_categorie: "",
    }),
    [selectedCategorie?.slug],
  );

  const resetKey = selectedCategorie?.slug ?? "";

  const {
    displayProducts,
    hasMore,
    isLoading,
    isFetching,
    isFetchingMore,
    loadMore,
  } = useInfiniteProductList(listQuery, resetKey);

  const sentinelRef = useInfiniteScrollSentinel(
    loadMore,
    hasMore,
    displayProducts.length,
  );

  const { data: categoriesData, isLoading: isLoadingCategories } =
    useGetAllCategoryListQuery();

  const catalogVendeur = getCatalogVendeur();

  const categories = sortByAlphabetical(categoriesData?.results ?? []);

  const handleSelectParent = (cat: any) => {
    setSelectedCategorie((prev: any) =>
      prev?.slug === cat?.slug ? null : cat
    );
  };

  const clearCategoryFilter = () => setSelectedCategorie(null);

  const handleCardClick = (prod: any) => {
    navigate(buildCatalogPath(`/produit/${prod?.slug}`));
  };

  const storeName =
    catalogVendeur?.nom_boutique ||
    catalogVendeur?.nom_de_la_boutique ||
    "Nom de la boutique";

  const categoryName = selectedCategorie?.nom?.trim() || "";
  const productsTitle = categoryName
    ? `Nos produits · ${categoryName}`
    : "Nos produits";
  const showProductsSkeleton =
    (isLoading || isFetching) && displayProducts.length === 0;

  return (
    <div className="template-wrapper">

      <TemplateNavbar vendeurName={storeName} />

      <div className="main-content">

        {/* ── Catégories ── */}
        <div className="categories-section">
          <h2 className="section-title">Nos catégories</h2>
        </div>
        <div className="categories-grid">
          {isLoadingCategories ? (
            <CategorySkeleton />
          ) : categories.length > 0 ? (
            categories.map((cat) => (
              <CategoryItem
                key={cat.id}
                item={cat}
                isActive={selectedCategorie?.id === cat?.id}
                onClick={() => handleSelectParent(cat)}
              />
            ))
          ) : (
            <p className="empty-message">Aucune catégorie trouvée.</p>
          )}
        </div>

        {/* ── Nos produits  ── */}
        <div className="template-products-col">
          <h2 className="section-title catalog-products-section-title">
            {productsTitle}
          </h2>

          {categoryName && (
            <div className="catalog-category-filter-pill">
              <span className="catalog-category-filter-pill__label">
                Filtre : {categoryName}
              </span>
              <button
                type="button"
                className="catalog-category-filter-pill__clear"
                onClick={clearCategoryFilter}
                aria-label="Retirer le filtre catégorie"
              >
                ×
              </button>
            </div>
          )}

          <div className="products-grid">
            {showProductsSkeleton ? (
              <ProductSkeleton />
            ) : displayProducts.length > 0 ? (
              displayProducts.map((prod, index) => (
                <ProductCard
                  key={
                    prod?.id != null
                      ? `product-${prod.id}-${index}`
                      : `product-fallback-${index}`
                  }
                  item={prod}
                  devise={catalogVendeur?.devise}
                  onCardClick={handleCardClick}
                />
              ))
            ) : (
              !showProductsSkeleton && (
                <p className="empty-message">Aucun produit trouvé.</p>
              )
            )}
          </div>

          <div
            ref={sentinelRef}
            className="infinite-scroll-sentinel"
            aria-hidden="true"
          />

          {isFetchingMore && hasMore && (
            <div className="d-flex justify-content-center py-3">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Chargement...</span>
              </div>
            </div>
          )}
        </div>

      </div>

    </div>
  );
}